import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Observable } from 'rxjs';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import { mensagemErroApi } from '../../../../utils/mensagem-erro-api';
import { Sistema } from '../../../../types/smartinteg';

@Component({
  selector: 'app-sistema-list',
  templateUrl: './sistema-list.component.html',
  styleUrls: ['./sistema-list.component.css'],
  providers: [ConfirmationService]
})
export class SistemaListComponent implements OnInit {

  sistemas: Sistema[] = [];
  loading: boolean = true;
  salvando: boolean = false;

  sistemaDialog: boolean = false;
  isEditDialog: boolean = false;
  sistemaEmEdicao: Sistema | null = null;

  form!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private smartintegService: SmartintegService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3)]],
      descricao: ['']
    });
    this.carregarSistemas();
  }

  carregarSistemas(): void {
    this.loading = true;
    this.smartintegService.listarSistemas().subscribe({
      next: (data) => {
        this.sistemas = data;
        this.loading = false;
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Erro ao carregar sistemas: ' + mensagemErroApi(err));
        this.loading = false;
      }
    });
  }

  novoSistema(): void {
    this.sistemaEmEdicao = null;
    this.isEditDialog = false;
    this.form.reset({ nome: '', descricao: '' });
    this.sistemaDialog = true;
  }

  editarSistema(sistema: Sistema): void {
    this.sistemaEmEdicao = sistema;
    this.isEditDialog = true;
    this.form.reset({ nome: sistema.nome, descricao: sistema.descricao ?? '' });
    this.sistemaDialog = true;
  }

  salvarSistema(): void {
    if (this.form.invalid) {
      this.toastService.alerta('Atenção', 'Preencha todos os campos obrigatórios');
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    const base = { nome: v.nome, descricao: v.descricao || undefined };
    this.salvando = true;

    const acao$: Observable<unknown> = this.isEditDialog
      ? this.smartintegService.atualizarSistema({ id: this.sistemaEmEdicao!.id, ...base })
      : this.smartintegService.criarSistema(base);

    acao$.subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', this.isEditDialog ? 'Sistema atualizado com sucesso' : 'Sistema criado com sucesso');
        this.salvando = false;
        this.sistemaDialog = false;
        this.carregarSistemas();
      },
      error: (err) => {
        this.toastService.erro('Erro', `Erro ao ${this.isEditDialog ? 'atualizar' : 'criar'} sistema: ` + mensagemErroApi(err));
        this.salvando = false;
      }
    });
  }

  // O backend não checa dependentes: sem esta verificação um processo ficaria apontando para um sistema excluído
  deletarSistema(sistema: Sistema): void {
    this.smartintegService.listarProcessos().subscribe({
      next: (processos) => {
        const usos = processos.filter(p => p.sistemaId === sistema.id);
        if (usos.length > 0) {
          this.toastService.alerta(
            'Atenção',
            `O sistema "${sistema.nome}" é usado por ${usos.length} processo(s). Mova ou exclua os processos antes de excluir o sistema.`
          );
          return;
        }
        this.confirmarExclusao(sistema);
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Erro ao verificar o uso do sistema: ' + mensagemErroApi(err));
      }
    });
  }

  private confirmarExclusao(sistema: Sistema): void {
    this.confirmationService.confirm({
      message: `Tem certeza que deseja deletar o sistema <strong>${sistema.nome}</strong>?`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Sim, Deletar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.loading = true;
        this.smartintegService.deletarSistema(sistema.id).subscribe({
          next: () => this.aposExcluir(sistema),
          error: (err) => {
            this.toastService.erro('Erro', 'Erro ao deletar sistema: ' + mensagemErroApi(err));
            this.loading = false;
          }
        });
      }
    });
  }

  // O backend responde 200 ao excluir mas hoje mantém o registro na listagem (pendência P2 do checklist)
  private aposExcluir(sistema: Sistema): void {
    this.smartintegService.listarSistemas().subscribe({
      next: (data) => {
        this.sistemas = data;
        this.loading = false;
        if (data.some(s => s.id === sistema.id)) {
          this.toastService.alerta('Atenção', `O backend confirmou a exclusão, mas o sistema "${sistema.nome}" continua listado.`);
        } else {
          this.toastService.sucesso('Sucesso', 'Sistema deletado com sucesso');
        }
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Erro ao recarregar sistemas: ' + mensagemErroApi(err));
        this.loading = false;
      }
    });
  }

  campoInvalido(campo: string): boolean {
    const control = this.form.get(campo);
    return !!(control && control.invalid && control.touched);
  }

  fecharDialog(): void {
    this.sistemaDialog = false;
  }
}
